// src/layout/builders/ProjectHold/HoldTable.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import useProjectStatus from "../../../store/builders/useProjectStatus";
import ProjectTable from "../ProjectTable";
import useProjectInfoStore from "../../../store/builders/useProjectInfoStore";

const allowedStatusesForHold = ['active', 'rejected'];

const HoldTable = () => {
  const navigate = useNavigate();
  const { projects, isLoading, fetchProjects, updateProjectStatus } = useProjectStatus();
  const { fetchProjectInfo, isLoading: infoLoading } = useProjectInfoStore();
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [projectDetails, setProjectDetails] = useState(null);

  useEffect(() => {
    fetchProjects("hold");
  }, [fetchProjects]);

  const handleStatusChange = async (projectId, newStatus, comment = null) => {
    await updateProjectStatus(projectId, newStatus, comment);
     switch(newStatus) {
      // ... (navigation logic remains the same) ...
        case 'active':
            navigate('/builder/liveproject');
            break;
        case 'hold':
            fetchProjects("hold"); // Refresh current view
            break;
        case 'completed':
            navigate('/builder/completedproject');
            break;
        case 'approved':
            navigate('/builder/pendingproject');
            break;
        case 'rejected':
            navigate('/builder/rejectionproject');
            break;
        case 'completion_request':
            navigate('/builder/completionrequest');
            break;
        default:
            fetchProjects("hold"); // Default refresh
    }
  };

  const handleViewComment = async (projectId) => {
    const details = await fetchProjectInfo(projectId);
    if (details) {
        setProjectDetails(details);
        setShowCommentModal(true);
    }
  };

  const combinedLoading = isLoading || infoLoading;

  if (combinedLoading && !projects.length) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <>
      {/* Comment History Modal */}
      {showCommentModal && projectDetails && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            {/* ... (modal content stays the same) ... */}
               <div className="bg-white rounded-lg p-5 sm:p-6 w-full max-w-lg shadow-xl transform transition-all">
                    <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Hold Comments History</h3>
                    <button
                        onClick={() => setShowCommentModal(false)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Close comment history"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    </div>
                    <div className="mb-3 text-sm">
                    <p><span className="font-medium text-gray-600">Project:</span> {projectDetails.project_name || 'N/A'}</p>
                    <p><span className="font-medium text-gray-600">Location:</span> {projectDetails.project_location || 'N/A'}</p>
                    </div>
                    <div className="max-h-[50vh] overflow-y-auto border-t border-b border-gray-200 divide-y divide-gray-100">
                    {projectDetails.holds && projectDetails.holds.length > 0 ? (
                        projectDetails.holds.map((hold) => (
                            <div key={hold.id} className="py-3 px-1">
                            <p className="text-gray-800 text-sm">{hold.comment}</p>
                            <p className="text-xs text-gray-500 mt-1">
                                {new Date(hold.created_at).toLocaleString()}
                            </p>
                            </div>
                        ))
                        ) : (
                            <p className="text-center text-gray-500 py-4 text-sm">No hold comments found.</p>
                        )
                    }
                    </div>
                    <div className="mt-4 flex justify-end">
                    <button
                        onClick={() => setShowCommentModal(false)}
                        className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-xs"
                    >
                        Close
                    </button>
                    </div>
                </div>
         </div>
      )}

      <ProjectTable
        title="Hold Projects"
        projects={projects}
        handleStatusChange={handleStatusChange}
        navigate={navigate}
        allowedNextStatuses={allowedStatusesForHold}
        showActionsColumn={true} // Keep actions column for View Comments
        // CHANGED: Define the button here
        extraActions={(project) => (
           project.status === "hold" && project.holds && project.holds.length > 0 ? (
             <button
                onClick={() => handleViewComment(project.id)}
                title="View Hold Comments"
                className="px-3 py-1 text-[10px] bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors duration-200"
             >
                View Comments
             </button>
           ) : null // Return null if not applicable
        )}
      />
    </>
  );
};

export default HoldTable; 